<script>
import {Locale} from "vue-i18n";
import {BoxPlotChart} from "@sgratzl/chartjs-chart-boxplot";
import annotationPlugin from "chartjs-plugin-annotation";
import constants from "@/libs/Constants";

BoxPlotChart.register(annotationPlugin);

export default {
  props: {
    propId: String,
    propLabels: Array,
    propDatasets: Array,

    //Axes
    propTitle: String,
    propTitleLegend: {type: String, default: null},
    propXAxisLabel: String,
    propYAxisLabel: String,
    propYMin: {type: Number, default: null},
    propYMax: {type: Number, default: null},
    propAnnotations: {type: Object, default: null},
    propLocale: {type: Locale, default: null},

    propDownloadFileName: {type: String, default: null},
    propDownloadToggle: {type: Boolean, default: false},
  },
  watch: {
    propDatasets(newValue, oldValue) {
      this.loadChart();
    },
    propLocale(newData, oldData) {
      this.loadChart();
    },
    propDownloadToggle(newValue, oldValue) {
      let canvas = this.boxplotChart.canvas;
      const ctx = canvas.getContext('2d');

      // Create a temporary white background
      const prevComposite = ctx.globalCompositeOperation;
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = prevComposite;

      // Generate image and download
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = this.propDownloadFileName !== null ? `${this.propDownloadFileName}.png` : 'plot.png';
      link.click();
    }
  },
  data() {
    return {
      boxplotChart: null
    }
  },
  methods: {
    loadChart() {
      if (this.propDatasets !== null && this.propDatasets.length > 0) {
        if (this.boxplotChart !== null) {
          this.boxplotChart.destroy();
          this.boxplotChart = null;
        }

        const ctx = document.getElementById(this.propId)?.getContext("2d");
        if (!ctx) return;

        this.boxplotChart = new BoxPlotChart(ctx, {
          data: this.chartData,
          options: this.chartOptions
        });
      }
    }
  },
  computed: {
    chartData() {
      return {
        labels: this.propLabels || [],
        datasets: this.propDatasets || []
      };
    },
    chartOptions() {
      //Title of chart
      let titleDisplay = false;
      let titleText = '';
      if (this.propTitle !== null && this.propTitle !== '' && this.propTitle !== undefined) {
        titleDisplay = true;
        titleText = this.propTitle;
      }

      //Title of legend
      let titleLegendDisplay = false;
      let titleLegendText = '';
      if (this.propTitleLegend !== null && this.propTitleLegend !== '' && this.propTitleLegend !== undefined) {
        titleLegendDisplay = true;
        titleLegendText = this.propTitleLegend;
      }


      let options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            min: this.propYMin,
            max: this.propYMax,
            title: {
              display: this.propYAxisLabel !== null && this.propYAxisLabel !== '',
              text: this.propYAxisLabel //Unit of measurement
            },
            border: {
              dash: (context) => context.tick.value === 0 ? [4, 4] : [], // Zero line
            },
            grid: {
              color: (context) => context.tick.value === 0 ? '#292929' : '#e5e5e5', // Zero line
              lineWidth: (context) => context.tick.value === 0 ? 2 : 1,
            },
          },
          x: {
            title: {
              display: this.propXAxisLabel !== null && this.propXAxisLabel !== '',
              text: this.propXAxisLabel //Unit of measurement
            },
            offset: true // Needed for better spacing
          }
        },
        plugins: {
          title: {
            display: titleDisplay,
            text: titleText
          },
          legend: {
            title: {
              display: titleLegendDisplay,
              text: titleLegendText,
              padding: 15
            },
            labels: {
              filter: function (item, chart) {
                // hide empty label dataset from legend
                return item.text !== constants.LABELS.NO_LABEL;
              }
            },
          },
          tooltip: {
            filter: (tooltipItem) => {
              // hide tooltip for datasets with specific (empty) label
              return tooltipItem.dataset.label !== constants.LABELS.NO_LABEL;
            }
          }
        }
      };

      if (this.propAnnotations !== null && this.propAnnotations !== undefined)
        options.plugins.annotation = {annotations: this.propAnnotations};

      return options;
    }
  },
  mounted() {
    this.loadChart();
  }
};
</script>

<template>
  <div class="row">
    <div class="col">
      <canvas :id="this.propId" class="boxplot"></canvas>
    </div>
  </div>
</template>

<style scoped>
.boxplot {
  width: 100%;
  height: 150px;
}
</style>
